import React, {useCallback} from 'react'
import {
  DIRECTOR_POSITION,
  DIRECTOR_UTILITY_BILL_DOCUMENT,
  DIRECTOR_DIRECTOR_IDENTIFICATION_NUMBER,
  DIRECTOR_PAN_CARD_DOCUMENT,
  COMPANY_DIRECTORS,
  PersonDocumentUploadWithInputContainer,
  InputPureWithInputContainer,
  shouldRenderDirectorPanCardDocumentFilter,
  ExistingPersonInputRow,
  AddressForm,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'
import {Row, Col, H5} from '@modifi/ui'

import {PoliticallyExposedInputRow} from '../input-rows'
import PersonForm from '../../../../../features/person-form'
import {
  FormInputComponentCol,
  FormUploadComponentCol,
  ExistingFilesList,
} from '../../../../../plugins/onboarding'
import {FormRow} from '../../../../../ui/form'
import ProgressSteps from '../../../../../component/progress-steps'
import {getCompanyRepProgressStepsLabels} from '../../../utils'
import {SelectOption, TranslateFn} from '../../../../../types/common'

type DirectorFormContentProps = {
  t: TranslateFn
  isNewDirector: boolean
  selectPersonAsDataSource: (value: string) => void
  dataSourceOptions: SelectOption[]
  currentPage: number
  hasAnyRoleFieldItem: boolean
}

const DirectorFormContent: React.FC<DirectorFormContentProps> = ({
  isNewDirector,
  selectPersonAsDataSource,
  t,
  dataSourceOptions,
  currentPage,
  hasAnyRoleFieldItem,
}) => {
  const showPersonForm = useCallback(() => {
    if (isNewDirector) {
      if (
        (dataSourceOptions.length > 0 && currentPage === 2) ||
        (dataSourceOptions.length < 1 && currentPage === 1)
      ) {
        return true
      }
    } else if (currentPage === 1) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length, isNewDirector])

  const showRoleFields = useCallback(() => {
    if (
      (currentPage === 2 && dataSourceOptions.length > 1) ||
      (currentPage === 1 && dataSourceOptions.length < 1)
    ) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length])

  const showResidenceFields = useCallback(() => {
    if (currentPage === 3 || (currentPage === 2 && dataSourceOptions.length < 1)) {
      return true
    }
    return false
  }, [currentPage, dataSourceOptions?.length])

  return (
    <>
      <ProgressSteps
        totalSteps={dataSourceOptions.length > 0 && isNewDirector && hasAnyRoleFieldItem ? 3 : 2}
        currentStep={currentPage}
        labelSteps={getCompanyRepProgressStepsLabels(dataSourceOptions.length > 0 && isNewDirector)}
      />

      {isNewDirector && dataSourceOptions.length > 0 && currentPage === 1 && (
        <ExistingPersonInputRow
          t={t}
          onSelectPerson={selectPersonAsDataSource}
          options={dataSourceOptions}
          personRole={COMPANY_DIRECTORS}
        />
      )}

      {showPersonForm() && (
        <>
          <PersonForm t={t} formType={COMPANY_DIRECTORS} />{' '}
        </>
      )}

      {showRoleFields() && (
        <>
          <FormRow mb={16}>
            <FormInputComponentCol
              t={t}
              inputName={DIRECTOR_POSITION}
              inputComponent={InputPureWithInputContainer}
              inputProps={{
                labelAddition: <InputTooltipInfo inputName={DIRECTOR_POSITION} t={t} />,
              }}
            />
            <FormInputComponentCol
              t={t}
              inputName={DIRECTOR_DIRECTOR_IDENTIFICATION_NUMBER}
              inputComponent={InputPureWithInputContainer}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={DIRECTOR_DIRECTOR_IDENTIFICATION_NUMBER} t={t} />
                ),
              }}
            />
          </FormRow>

          <PoliticallyExposedInputRow t={t} />

          <FormRow>
            <FormUploadComponentCol
              t={t}
              inputName={DIRECTOR_PAN_CARD_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              filterFunc={shouldRenderDirectorPanCardDocumentFilter}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: <InputTooltipInfo inputName={DIRECTOR_PAN_CARD_DOCUMENT} t={t} />,
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />
          </FormRow>
        </>
      )}

      {showResidenceFields() && (
        <>
          <Row mb={16} mt={8}>
            <Col>
              <H5>{t('person-form.address.headline')}</H5>
            </Col>
          </Row>
          <Row>
            <Col>
              <AddressForm
                formType={COMPANY_DIRECTORS}
                components={{
                  salutation: null,
                  firstName: null,
                  middleName: null,
                  lastName: null,
                }}
              />
            </Col>
          </Row>
          <FormRow>
            <FormUploadComponentCol
              t={t}
              inputName={DIRECTOR_UTILITY_BILL_DOCUMENT}
              uploadFormComponent={PersonDocumentUploadWithInputContainer}
              colProps={{md: 12}}
              inputProps={{
                labelAddition: (
                  <InputTooltipInfo inputName={DIRECTOR_UTILITY_BILL_DOCUMENT} t={t} />
                ),
                components: {
                  existingFilesList: ExistingFilesList,
                },
              }}
            />
          </FormRow>
        </>
      )}
    </>
  )
}

export default DirectorFormContent
